<!-- Global route-progress bar (guards, resolvers, lazy chunks): every surface,
     click-through, pinned under the safe area. -->
@if (navProgress.visible()) {
  <crwd-progress-chip placement="top" data-testid="navigation-progress" [ariaLabel]="'common.loading' | translate" />
}
<!-- Just-in-time shared-folder grant cards (11-local-files.md). Renders nothing
     outside the desktop shell. -->
<app-local-files-grant-banner />
<!-- Concepts tour (Chats, Topics, Agents, Teams): opens by itself once for a
     NEW user only (OnboardingTourService), reopens from the user menu. Only
     over the signed-in shell with an org loaded (review T2): never during
     sign-up, org creation or email verification, when there is no sidebar for
     the slides to describe. -->
@if (tourCanShow) {
  <app-onboarding-tour />
}
<!-- One-click browser-notification permission (real gesture — the boot request is
     answered by Chrome's quiet prompt). Signed-in shell only; hides itself once decided. -->
@if (appHeader.isHeaderVisible) {
  <app-notifications-nudge />
}

<div class="app-root" [class.with-shell]="appHeader.isHeaderVisible">
  <!-- The frame is the kit's: dynamic viewport minus the safe-area insets (the WebView draws edge to edge),
       the insets as padding, and the drawer toggle (below 1024px, only while the shell's rail is rendered) with
       the 3rem content band under it unless the surface is flush (AppHeaderService.isContentFlush). connect's
       panes scroll themselves (transcripts, rosters) and the page never does. -->
  <crwd-app-shell
    contentHeight="dynamic-viewport"
    safeAreaPadding
    [drawerToggle]="appHeader.isHeaderVisible"
    [contentFlush]="appHeader.isContentFlush"
    [drawerToggleLabel]="'sidebar.open-navigation' | translate"
    (drawerTogglePressed)="appHeader.mobileMenuOpen = !appHeader.mobileMenuOpen"
  >
    <!-- Blocked LOCAL agent tool calls waiting on a human (desktop shell only;
         renders nothing elsewhere). Above the low-balance banner: a frozen agent
         is more urgent than a billing nudge. -->
    <!-- The organizations you have open, as tabs (Vincent, 2026-10-21) — the
         org lives in the URL (/o/{orgId}), so each browser tab can be in its own
         org. Multi-org people only: with one org there is nothing to switch.
         Not where the sidebar is a drawer (the mobile app, or the web below
         1024px — phone and tablet): there a banner row sits above the app
         header, so it lives in the drawer instead. -->
    @if (nav.multiOrg && appHeader.isHeaderVisible && !nav.orgTabsInDrawer) {
      <app-org-tabs shell-banner></app-org-tabs>
    }

    <app-local-agent-approvals shell-banner></app-local-agent-approvals>

    <!-- The member's monthly budget: paused (hard stop) with "Request more", or the near-limit warning. -->
    <app-member-budget-banner shell-banner></app-member-budget-banner>

    @if (nav.lowBalanceVisible) {
      <crwd-banner
        shell-banner
        tone="attention"
        dismissible
        [actionLabel]="'sidebar.manage-billing' | translate"
        [dismissAriaLabel]="'common.buttons.close' | translate"
        (action)="nav.openBilling()"
        (dismissed)="nav.dismissLowBalance()"
      >
        {{ nav.lowBalanceMessage }}
      </crwd-banner>
    }

    <!-- crwd-sidebar must be a DIRECT child: the shell projects it by element
         selector and queries it with contentChild. That is why there is no
         longer a rail component wrapping it — see SidebarNavService. -->
    @if (appHeader.isHeaderVisible) {
      <crwd-sidebar
        #sidebar
        [items]="nav.navItems()"
        wordmarkLightSrc="assets/image/wordmark-dark.png"
        wordmarkDarkSrc="assets/image/wordmark.png"
        [whiteLabel]="nav.whiteLabel.brand() ?? null"
        drawerBelow="desktop"
        drawerWidth="full"
        centerBrandInDrawer
        [(mobileOpen)]="appHeader.mobileMenuOpen"
        [navAriaLabel]="'sidebar.dashboard' | translate"
        [helpLabel]="'menu.support' | translate"
        (helpClick)="nav.openSupport()"
        (itemSelected)="nav.onNavItem($event, sidebar)"
        (groupToggled)="nav.onGroupToggled($event)"
        (sectionAction)="nav.onSectionAction($event)"
        (sectionMenuSelected)="nav.onSectionMenu($event)"
      >
        <!-- Drawer widths (the mobile app, or the web below 1024px): the org tabs
             at the top of the drawer instead of a banner row (see above). -->
        @if (nav.multiOrg && nav.orgTabsInDrawer) {
          <app-org-tabs sidebar-switcher></app-org-tabs>
        }

        <!-- crwd-user-button: Account + Sign out, and the open org's balance /
             Buy more / Manage organization (admin-only — they open the admin
             console). The org switcher left the rail for the org tabs on top. -->
        <crwd-user-button
          [name]="nav.userName"
          [detail]="nav.organization.data?.name || ''"
          [avatarSrc]="nav.user.data?.profilePictureUrl || ''"
          [credits]="nav.organization.data?.creditBalance ?? null"
          [showBuyCredits]="nav.isOrgAdmin"
          [creditsLabel]="'menu.credits_left' | translate"
          [buyCreditsLabel]="'menu.buy_more' | translate"
          [manageOrgLabel]="nav.isOrgAdmin ? ('menu.manage_organization' | translate) : ''"
          [accountLabel]="'menu.account' | translate"
          [signOutLabel]="'menu.logout' | translate"
          (buyCredits)="nav.buyMore()"
          (manageOrgClick)="nav.manageOrganization()"
          (accountClick)="nav.goToAccount()"
          (signOut)="nav.logout()"
        >
          <!-- "Take the tour" reopens the concepts tour (OnboardingTourService). -->
          <crwd-user-menu-extra>
            @if (memberBudget.meter(); as meter) {
              <crwd-user-menu-item
                data-testid="user-menu-budget"
                icon="wallet"
                keepMenuOpen
                [label]="meter.key | translate: meter.params"
              />
            }
            <crwd-user-menu-item
              data-testid="user-menu-tour"
              icon="play-circle"
              [label]="'sections.tour.take' | translate"
              (pressed)="tour.reopen()"
            />
          </crwd-user-menu-extra>
        </crwd-user-button>
      </crwd-sidebar>
    }

    <div class="app-content">
      <router-outlet></router-outlet>
    </div>
  </crwd-app-shell>

  <!-- The support panel is created HERE, on first open, by a dynamic import
       (see AppComponent.loadSupportPanel). It pulls the kit markdown renderer
       and with it highlight.js — 1.8 MB that has no business in the initial
       bundle for a panel most sessions never open. `@defer` cannot do this
       job: AppComponent is NgModule-based, so it has no `imports` array for
       the block to defer from, and dropping the component from the module
       just leaves an inert unknown element (CUSTOM_ELEMENTS_SCHEMA hides it). -->
  <ng-container #supportHost></ng-container>

  <!-- THE search: one palette over chats, agents, teams and messages, on every
       route. Opened by the rail Search row and Cmd/Ctrl+K (GlobalSearchService). -->
  <app-global-search></app-global-search>

  <!-- A newer deploy is live but this tab hasn't navigated (navigation
       auto-reloads): give the parked tab a visible way onto the new build.
       Mounting the kit component also starts the version poll — which is why
       it is gated: with the service worker running SwUpdate owns version
       changes (AppVersionService), and in a packaged shell /version.json ships
       inside the binary and can never change. See versionPollEnabled. -->
  @if (versionPollEnabled) {
    <crwd-app-update
      [label]="'app_update.available' | translate"
      [actionLabel]="'app_update.refresh' | translate"
    ></crwd-app-update>
  } @else if (versionPillEnabled) {
    <!-- Service-worker clients (production): the worker downloads the new
         version in the background; once it is fully cached the same pill offers
         "Refresh" (the kit's external mode: AppVersionService owns the check). -->
    <crwd-app-update
      mode="external"
      [visible]="appVersion.ready()"
      [label]="'app_update.available' | translate"
      [actionLabel]="'app_update.refresh' | translate"
      (pressed)="appVersion.activateUpdate()"
    ></crwd-app-update>
  }
  <!-- Every redirect that leaves this screen (RedirectService): the same full-screen loader as the boot
       splash (three dots) with the label under it. It always clears (the service's 20 s ceiling, back
       navigation, returning from the external page). A failure becomes a kit toast with Try again. -->
  @if (redirect.state(); as r) {
    <div class="redirect-screen" data-testid="redirect-chip" role="status" aria-live="polite" aria-busy="true">
      <div class="redirect-screen__dots" aria-hidden="true"><span></span><span></span><span></span></div>
      <p class="redirect-screen__label">{{ r.label | translate }}</p>
    </div>
  }

  <!-- The DESKTOP app's own update (the Electron shell): "Restart now" once it has
       downloaded, dismissable — it stays in the tray. Renders nothing elsewhere. -->
  <app-desktop-update-pill></app-desktop-update-pill>
</div>
